import { Star, ExternalLink } from "lucide-react";
import { useLang } from "../i18n";
import { Reveal, Kicker } from "./Reveal";

export const GOOGLE_MAPS_URL = "https://maps.app.goo.gl/Hqucrr3uoWLqKBrG8";

const REVIEWS = [
  {
    name: "DJ Peralta",
    avatar: "https://lh3.googleusercontent.com/a-/ALV-UjVMMJ36x_rGxgZWrYaXLK0D6Ws2YpOmti8YK-Z5kF1m8flPG8Q=w36-h36-p-rp-mo-br100",
    date: { es: "hace 3 semanas", ca: "fa 3 setmanes" },
    es: "Servicio excelente. Mi coche estaba hecho un desastre de la mudanza y el trabajo, y me lo dejó mejor que cuando lo compré. Una persona muy profesional y cercana.",
    ca: "Servei excel·lent. El meu cotxe estava fet un desastre de la mudança i la feina, i me'l va deixar millor que quan el vaig comprar. Una persona molt professional i propera.",
  },
  {
    name: "Adrián",
    avatar: "https://lh3.googleusercontent.com/a-/ALV-UjVDBMkbSWi_YntGsAdWsFO89OmVqAfmHT7BRie80HESbWlbeph5=w36-h36-p-rp-mo-br100",
    date: { es: "hace 1 mes", ca: "fa 1 mes" },
    es: "¡Los recomiendo totalmente! ¡Estoy encantado! Pensaba que el interior de mi coche no tenía arreglo por el moho, ¡y lo dejaron como nuevo! Además, ¡lo hicieron en mi entrada!",
    ca: "Els recomano totalment! Estic encantat! Pensava que l'interior del meu cotxe no tenia arranjament pel floridura, i el van deixar com nou! A més, ho van fer a casa meva!",
  },
  {
    name: "Izan6905",
    avatar: "https://lh3.googleusercontent.com/a/ACg8ocKaqhudb7N9jncMI7rBm3vi220mp9v60J7EIAfqo7LmGeO1Hg=w36-h36-p-rp-mo-br100",
    date: { es: "hace 2 meses", ca: "fa 2 mesos" },
    es: "Un servicio de 10. Me dejó el coche impecable, por dentro y por fuera, cuidando cada detalle. Incluso me lo entregó en casa, algo que agradecí mucho.",
    ca: "Un servei de 10. Em va deixar el cotxe impecable, per dins i per fora, cuidant cada detall. Fins i tot me'l va lliurar a casa, cosa que vaig agrair molt.",
  },
];

function Stars() {
  return (
    <span className="flex gap-0.5" aria-label="5 de 5 estrellas">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} className="h-4 w-4 fill-[#63E51D] text-[#63E51D]" />
      ))}
    </span>
  );
}

export default function Reviews() {
  const { lang, t } = useLang();

  return (
    <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-12 lg:py-32" data-testid="home-reviews-section">
      <div className="mb-14 flex flex-wrap items-end justify-between gap-6">
        <div>
          <Reveal><Kicker>{t("home.reviewsKicker")}</Kicker></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-4 font-display text-2xl font-bold uppercase tracking-tight text-white sm:text-3xl lg:text-5xl">
              {t("home.reviewsTitle")}
            </h2>
          </Reveal>
        </div>
        <Reveal delay={0.2}>
          <a
            href={GOOGLE_MAPS_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="reviews-google-summary"
            className="flex items-center gap-3 rounded-xl border border-white/10 bg-[#121212] px-5 py-4 transition-all duration-300 hover:border-[#63E51D]/50"
          >
            <span className="font-display text-3xl font-black text-white">5,0</span>
            <span>
              <Stars />
              <span className="mt-1 block text-xs text-zinc-400">{t("reviews.basedOn")}</span>
            </span>
          </a>
        </Reveal>
      </div>

      <div className="grid gap-5 md:grid-cols-3">
        {REVIEWS.map((r, i) => (
          <Reveal key={r.name} delay={i * 0.08}>
            <figure data-testid={`review-card-${i}`} className="card-sheen flex h-full flex-col rounded-xl border border-white/10 bg-[#121212] p-7 transition-all duration-300 hover:-translate-y-1 hover:border-[#63E51D]/40 hover:shadow-[0_0_25px_rgba(99,229,29,0.12)]">
              <Stars />
              <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-zinc-300">“{r[lang] || r.es}”</blockquote>
              <figcaption className="mt-6 flex items-center gap-3 border-t border-white/5 pt-5">
                <img src={r.avatar} alt={r.name} loading="lazy" className="h-9 w-9 rounded-full object-cover" />
                <span>
                  <span className="block text-sm font-semibold text-white">{r.name}</span>
                  <span className="block text-xs text-zinc-500">{r.date[lang] || r.date.es} · Google</span>
                </span>
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.2}>
        <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
          <a
            href={GOOGLE_MAPS_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="reviews-read-all-button"
            className="inline-flex items-center gap-2 rounded-full border border-white/15 px-6 py-3 text-sm font-semibold text-white transition-all duration-300 hover:border-[#63E51D] hover:text-[#8BFF32]"
          >
            {t("reviews.readAll")} <ExternalLink className="h-4 w-4" />
          </a>
          <a
            href={GOOGLE_MAPS_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="reviews-write-button"
            className="inline-flex items-center gap-2 rounded-full bg-[#63E51D] px-6 py-3 text-sm font-bold text-black transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_0_20px_rgba(99,229,29,0.4)]"
          >
            <Star className="h-4 w-4 fill-black" /> {t("home.reviewsGmb")}
          </a>
        </div>
      </Reveal>
    </section>
  );
}
